/** @jsxImportSource @emotion/react */
import {
  Accordion,
  Button,
  Checkbox,
  Flex,
  Form,
  Link,
  List,
  LoadingIcon,
  Message,
  Stack,
  Text,
} from "basis";
import { DeleteCardModal } from "components/WaysToPay/PaymentMethods/DebitCard/DeleteCardModal";
import { getAmountError } from "components/WaysToPay/PaymentMethods/DebitCard/getAmountError";
import { getIsEligibleForDebitCard } from "components/WaysToPay/PaymentMethods/DebitCard/getIsEligilbeForDebitCard";
import { getIsPaymentInstrumentExpired } from "components/WaysToPay/PaymentMethods/DebitCard/getIsPaymentInstrumentExpired";
import { InEligibleAccountMessage } from "components/WaysToPay/PaymentMethods/DebitCard/InEligibleAccountMessage";
import { ProcessingPayment } from "components/WaysToPay/PaymentMethods/DebitCard/ProcessingPayment";
import { SavedCardSelection } from "components/WaysToPay/PaymentMethods/DebitCard/SavedCardSelection";
import { useDeletePayment } from "components/WaysToPay/PaymentMethods/DebitCard/useDeletePayment";
import { STATIC_RESOURCES } from "constants/contacts";
import { Account } from "library/accounts/types";
import { PaymentInstrument } from "library/api/payments";
import { useDebitCardPayment } from "library/hooks/useDebitCardPayment/useDebitCardPayment";
import { usePaymentInstruments } from "library/hooks/usePaymentInstruments";
import { formatAmount } from "library/moneyUtils";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

interface DebitCardProps {
  product: {
    payment_methods: {
      debit_card: {
        westpac_qvalent_code: string;
      };
    };
    region: "AU" | "NZ";
  };
  commonData: {
    accountId: string;
    lastFour: string;
  };
  account?: Account;
  amount: number;
  productName: string;
}

const paymentInstrumentComparator = (
  a: PaymentInstrument,
  b: PaymentInstrument,
) => {
  if (getIsPaymentInstrumentExpired(a)) {
    return 1;
  }

  if (getIsPaymentInstrumentExpired(b)) {
    return -1;
  }

  if (a.last_used_at > b.last_used_at) {
    return -1;
  }

  return 1;
};

const RegionToLegalName = {
  AU: "Latitude Finance Australia",
  NZ: "Gem Finance",
};

export const DebitCard = ({
  product,
  commonData,
  amount,
  account,
  productName,
}: DebitCardProps) => {
  const {
    payment_methods: { debit_card: debitCard },
    region,
  } = product;

  // need to show stored cards with no payment option for ineligible accounts
  const isEligibleForDebitCardPayment =
    !account || getIsEligibleForDebitCard(account);

  const { accountId, lastFour } = commonData;

  const {
    submitPayment,
    error,
    status,
    quickStreamRef,
    data: debitCardData,
  } = useDebitCardPayment({
    accountId,
    amount,
    region,
    supplierBusinessCode: debitCard.westpac_qvalent_code,
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (error?.type === "CRITICAL") {
      navigate("/ways-to-pay/error", {
        state: {
          card: {
            cardName: productName,
            cardEndsWith: lastFour,
          },
          returnLink: {
            href: window.location.href,
            label: "Go back and try again",
          },
        },
      });
    }
  }, [error, navigate, lastFour, productName]);

  useEffect(() => {
    if (debitCardData) {
      const { receiptNumber, cardNumber } = debitCardData;
      navigate("/ways-to-pay/debit-card/success", {
        state: {
          product: {
            cardName: productName,
            cardEndsWith: lastFour,
          },
          amount,
          receiptNumber,
          paymentDate: new Date().toISOString(),
          debitCardEndsWith: cardNumber,
        },
      });
    }
  });

  const onSubmitNewDebitCard = ({ values }) => {
    const { saveCardDetails } = values;

    submitPayment({ saveCard: saveCardDetails });
  };

  const displayAmount = formatAmount(amount, { withCents: true });

  const {
    isLoading: isFetchingPaymentInstruments,
    refetch,
    data,
  } = usePaymentInstruments();

  const paymentInstruments = useMemo(
    () =>
      data
        ? data.data.data.filter(({ card_region }) => card_region === region)
        : [],
    [data, region],
  );
  const hasSavedCard = paymentInstruments.length !== 0;

  const sortedPaymentInstruments = useMemo(
    () => paymentInstruments.sort(paymentInstrumentComparator),
    [paymentInstruments],
  );

  const [selectedPaymentInstrument, setSelectedPaymentInstrument] =
    useState("");

  useEffect(() => {
    if (selectedPaymentInstrument === "") {
      const availableInstruments = sortedPaymentInstruments.filter(
        (instrument) => !getIsPaymentInstrumentExpired(instrument),
      );
      setSelectedPaymentInstrument(
        availableInstruments[0]?.instrument_id ?? "",
      );
    }
  }, [sortedPaymentInstruments, selectedPaymentInstrument]);

  const onSubmitSavedDebitCard = () => {
    const savedInstrument = paymentInstruments.find(
      ({ instrument_id }) => instrument_id === selectedPaymentInstrument,
    );

    if (savedInstrument) {
      submitPayment({
        savedInstrument: {
          id: selectedPaymentInstrument,
          cardNumber: savedInstrument.last_4_digits,
          cardScheme: savedInstrument.card_scheme,
        },
      });
    }
  };

  const [removeInstrumentId, setRemoveInstrumentId] = useState("");
  const instrumentToRemove = useMemo(
    () =>
      paymentInstruments.find(
        ({ instrument_id }) => instrument_id === removeInstrumentId,
      ),
    [paymentInstruments, removeInstrumentId],
  );

  const { mutateAsync, isLoading: isDeletingPayment } = useDeletePayment();

  const deletePayment = async () => {
    const id = removeInstrumentId;
    setRemoveInstrumentId("");
    await mutateAsync(id);
    refetch();
    if (selectedPaymentInstrument === id) {
      setSelectedPaymentInstrument("");
    }
  };

  const amountError = getAmountError(amount);

  const isLoading = isDeletingPayment || isFetchingPaymentInstruments;

  const agreementUrl =
    region === "AU"
      ? STATIC_RESOURCES.debitCardServiceAgreementAu
      : STATIC_RESOURCES.debitCardServiceAgreementNz;

  if (!isEligibleForDebitCardPayment) {
    return (
      <Stack gap="6">
        <InEligibleAccountMessage region={region} />
        {hasSavedCard && (
          <Accordion color="white">
            <Accordion.Item initiallyOpen>
              <Accordion.Item.Header>
                Manage saved debit card(s)
              </Accordion.Item.Header>
              <Accordion.Item.Content>
                <SavedCardSelection
                  paymentInstruments={sortedPaymentInstruments}
                  selectedInstrumentId={selectedPaymentInstrument}
                  onSelect={(id) => setSelectedPaymentInstrument(id)}
                  onRemove={(id) => {
                    setRemoveInstrumentId(id);
                  }}
                />
              </Accordion.Item.Content>
            </Accordion.Item>
          </Accordion>
        )}
      </Stack>
    );
  }
  return (
    <>
      {status === "ERROR" && (
        <Message role="alert" severity="blocking" bg="secondary.pink.t30">
          {error?.message}
        </Message>
      )}
      {amountError && (
        <Message role="alert" severity="blocking" bg="secondary.pink.t30">
          {amountError}
        </Message>
      )}
      {status === "PROCESSING_PAYMENT" && <ProcessingPayment />}
      {isLoading && !amountError && (
        <Flex height="200" placeItems="center">
          <LoadingIcon size="medium" />
        </Flex>
      )}
      {!isLoading && !amountError && (
        <Accordion color="white">
          {hasSavedCard && (
            <Accordion.Item initiallyOpen>
              <Accordion.Item.Header>
                Pay with saved debit card(s)
              </Accordion.Item.Header>
              <Accordion.Item.Content>
                <Form initialValues={{}} onSubmit={onSubmitSavedDebitCard}>
                  <SavedCardSelection
                    paymentInstruments={sortedPaymentInstruments}
                    selectedInstrumentId={selectedPaymentInstrument}
                    onSelect={(id) => setSelectedPaymentInstrument(id)}
                    onRemove={(id) => {
                      setRemoveInstrumentId(id);
                    }}
                  />
                  <Button
                    type="submit"
                    testId="submit-saved-payment"
                    loading={status === "PROCESSING_PAYMENT"}
                    disabled={!selectedPaymentInstrument}
                  >
                    Pay {displayAmount}
                  </Button>
                </Form>
              </Accordion.Item.Content>
            </Accordion.Item>
          )}
          <Accordion.Item initiallyOpen={!hasSavedCard}>
            <Accordion.Item.Header>
              Pay with new debit card
            </Accordion.Item.Header>
            <Accordion.Item.Content color="white">
              {status === "LOADING" && (
                <Flex height="200" placeItems="center">
                  <LoadingIcon size="medium" />
                </Flex>
              )}
              <Stack gap="4">
                <Text textStyle="legal">
                  You can pay using your debit card, as long as it has been
                  issued by a local bank. Unfortunately, we can’t accept payment
                  from international debit cards.
                </Text>
                <div ref={quickStreamRef} />
              </Stack>
              <Form
                initialValues={{ saveCardDetails: false }}
                onSubmit={onSubmitNewDebitCard}
              >
                <Stack gap="4">
                  <Checkbox
                    name="saveCardDetails"
                    label="Save card details for future payment"
                    hideLabel
                    optional
                  >
                    Save card details for future payment
                  </Checkbox>
                  <Text>
                    By tapping <strong>Pay {displayAmount}</strong>:
                  </Text>
                  <List>
                    <List.Item>
                      <Text textStyle="legal">
                        I authorise and request {RegionToLegalName[region]} to
                        debit my nominated debit card for the payment amount
                        specified above.
                      </Text>
                    </List.Item>
                    <List.Item>
                      <Text textStyle="legal">
                        I agree to the terms and conditions of the{" "}
                        <Link href={agreementUrl} newTab>
                          Debit Card Service Agreement.
                        </Link>
                      </Text>
                    </List.Item>
                  </List>
                  <Button
                    type="submit"
                    testId="submit-payment"
                    loading={status === "PROCESSING_PAYMENT"}
                  >
                    Pay {displayAmount}
                  </Button>
                </Stack>
              </Form>
            </Accordion.Item.Content>
          </Accordion.Item>
        </Accordion>
      )}
      {instrumentToRemove && (
        <DeleteCardModal
          isOpen={!!instrumentToRemove}
          instrument={instrumentToRemove}
          onClose={() => setRemoveInstrumentId("")}
          onConfirm={() => deletePayment()}
        />
      )}
    </>
  );
};
